// src/components/FarmMark/FarmMarkLeaderboard.js

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { SlidersHorizontal } from "lucide-react";
import { TIME_RANGES } from "../../constants/farmMarkConstants";
import { getLeaderboardByCategory } from "../../services/farmMarkService";
import { IMPACT_LEADERBOARD_CATEGORIES } from "../../services/impactPointsService";
import LeaderboardEntry from "./LeaderboardEntry";
import FarmMarkFilterSheet from "./FarmMarkFilterSheet";
import LoadingSpinner from "../ui/LoadingSpinner";
import NoData from "../NoData";

const OVERALL_LEADERBOARD_KEY = "overall";

const DEFAULT_FILTERS = {
  category: OVERALL_LEADERBOARD_KEY,
  timeRange: TIME_RANGES.ALL_TIME,
};

export default function FarmMarkLeaderboard() {
  const { t } = useTranslation("farmMark");
  const [category, setCategory] = useState(DEFAULT_FILTERS.category);
  const [timeRange, setTimeRange] = useState(DEFAULT_FILTERS.timeRange);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);

  const activeFilterCount = (() => {
    let count = 0;
    if (category !== OVERALL_LEADERBOARD_KEY) count += 1;
    if (timeRange !== TIME_RANGES.ALL_TIME) count += 1;
    return count;
  })();

  // Ref-based cancellation: the effect cleanup must not return a value,
  // so the async fetch signals cancellation through this ref instead.
  const cancelledRef = useState({ current: false })[0];

  const fetchRows = useCallback(async () => {
    cancelledRef.current = false;
    setLoading(true);
    try {
      const result = await getLeaderboardByCategory(category, timeRange);
      if (!cancelledRef.current) {
        setRows(result || []);
        setLoading(false);
      }
    } catch {
      if (!cancelledRef.current) {
        setRows([]);
        setLoading(false);
      }
    }
  }, [category, timeRange]);

  useEffect(() => {
    fetchRows();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchRows]);

  const openFilters = useCallback(() => {
    setDraftFilters({ category, timeRange });
    setFilterOpen(true);
  }, [category, timeRange]);

  const applyFilters = useCallback(() => {
    setCategory(draftFilters.category);
    setTimeRange(draftFilters.timeRange);
    setFilterOpen(false);
  }, [draftFilters]);

  const resetFilters = useCallback(() => {
    setDraftFilters(DEFAULT_FILTERS);
    setCategory(DEFAULT_FILTERS.category);
    setTimeRange(DEFAULT_FILTERS.timeRange);
    setFilterOpen(false);
  }, []);

  return (
    <div>
      {/* Header row: title + Filter/Controls */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
            {t("leaderboard.title", { defaultValue: "Leaderboard" })}
          </h2>
        </div>
        <button
          type="button"
          onClick={openFilters}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
          aria-haspopup="dialog"
          aria-expanded={filterOpen}
          aria-label={t("filters.controls", { defaultValue: "Filters and controls" })}
          title={t("filters.controls", { defaultValue: "Filters and controls" })}
        >
          <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
          {activeFilterCount > 0 ? (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white bg-green-700 rounded-full">
              {activeFilterCount}
            </span>
          ) : null}
        </button>
      </div>

      <FarmMarkFilterSheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
        draft={draftFilters}
        setDraft={setDraftFilters}
        t={t}
      />

      {loading ? (
        <div className="py-12">
          <LoadingSpinner size="medium" />
        </div>
      ) : rows.length === 0 ? (
        <NoData
          title={t("leaderboard.noDataTitle", { defaultValue: "No ranked users yet" })}
          description={t("leaderboard.noDataDesc", { defaultValue: "Points will appear here as validated actions are completed." })}
        />
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-neutral-800">
          {rows.map((user, i) => (
            <LeaderboardEntry
              key={user.userId}
              rank={i + 1}
              user={user}
            />
          ))}
        </div>
      )}
    </div>
  );
}